<template>
  <div class="flex-shrink-0" aria-live="polite" aria-atomic="false">
    <transition
      name="news-bar-collapse"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
    >
      <div v-if="showBar" class="news-bar-collapse">
        <section
          class="news-bar"
          :style="barStyle"
          :aria-label="$t('app.news_bar_label')"
        >
          <p class="news-bar__content">
            <span class="news-bar__badge" 
              v-if="news.TextoTag" 
              :style="badgeStyle"
              >{{ news.TextoTag }}
            </span>

            <span class="news-bar__text">{{ segments.before }}<a
              v-if="segments.link"
              :href="linkUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="news-bar__link"
              @click="clickLink"
            >{{ segments.link }}<span class="sr-only"> {{ $t('app.opens_new_window') }}</span></a>{{ segments.after }}</span>
          </p>

          <div class="news-bar__actions">
            <button
              type="button"
              class="news-bar__btn"
              :aria-label="$t('app.news_close')"
              @click="dismiss"
            >
              <base-icon name="cross" size="xsm" :ariaHidden="true" />
            </button>
          </div>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
import { i18n } from '@/i18n'
import { findFirstFocusable, focusPreservingScroll } from '@/utils/routeA11yFocus'
import { TIPOS_NEWS_BAR } from '@/config/tipos-news-bar'
import { getNews } from '@/services/app/getNews'
import { clickbannernoticias } from '@/services/app/clickbannernoticias'
import { cerrarbannernoticias } from '@/services/app/cerrarbannernoticias'

const TAG_ENLACE = '#ENLACE#'
function sanitizeUrl (value) {
  const url = typeof value === 'string' ? value.trim() : ''
  return /^https?:\/\//i.test(url) ? url : null
}

export default {
  name: 'news-bar',
  data: function () {
    return {
      news: null,
      initTimer: null
    }
  },
  computed: {
    loggedIn: function () {
      return this.$store.getters['auth/loggedIn']
    },
    accessToken: function () {
      return this.$store.getters['auth/accessToken']
    },
    showBar: function () {
      return this.loggedIn && !!(this.news && this.news.TextoMensaje)
    },
    linkUrl: function () {
      return sanitizeUrl(this.news && this.news.Enlace)
    },
    idMensaje () {
      return this.news && this.news.idMensaje
    },
    badgeStyle: function () {
      const tipo = TIPOS_NEWS_BAR.find(t => t.id === (this.news && this.news.idTipoMensaje))
      return tipo 
        ? { backgroundColor: tipo.tagBackgroundColor, color: tipo.tagTextColor } 
        : { backgroundColor: 'var(--primary-800)' }
    },
    barStyle: function () {
      const tipo = TIPOS_NEWS_BAR.find(t => t.id === (this.news && this.news.idTipoMensaje))
      return tipo ? { backgroundColor: tipo.backgroundColor, color: tipo.textColor } : {}
    },
    // El enlace solo cubre el trozo de texto que sustituye a TAG_ENLACE dentro del mensaje.
    segments: function () {
      const news = this.news || {}
      const parts = (news.TextoMensaje || '').split(TAG_ENLACE)
      if (!this.linkUrl || !news.TextoEnlace || parts.length < 2) {
        return { before: news.TextoMensaje || '', link: null, after: '' }
      }
      return {
        before: parts[0],
        link: news.TextoEnlace,
        after: parts.slice(1).join(TAG_ENLACE)
      }
    }
  },
  created: function () {
    this.initTimer = setTimeout(this.fetchNews, 2000)
  },
  beforeDestroy: function () {
    clearTimeout(this.initTimer)
  },
  watch: {
    // Refrescamos las noticias cuando haya refresco
    accessToken: function () {
      this.fetchNews()
    }
  },
  methods: {
    fetchNews: async function () {
      if (!this.loggedIn) return
      try {
        const response = await getNews()
        if (response.blnError) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
        } else {
          this.news = response.DatosBanner
        }
      } catch (error) {
        console.error(error)
      }
    },
    clickLink: async function () {
      try {
        const data = await clickbannernoticias({ idMensaje: this.idMensaje })
        if (data.blnError) {
          console.error(i18n.t('feedback.error.formated_api_response_error', data))
        }
      } catch (error) {
        console.error(error)
      }
    },
    dismiss: async function () {
      const idMensaje = this.idMensaje
      this.news = null
      // WCAG 2.4.3: el botón pulsado desaparece, hay que reubicar el foco.
      this.$nextTick(() => {
        const target = findFirstFocusable(document.querySelector('main'))
        if (target) focusPreservingScroll(target)
      })
      try {
        const data = await cerrarbannernoticias({ idMensaje: idMensaje })
        if (data.blnError) {
          console.error(i18n.t('feedback.error.formated_api_response_error', data))
        } 
      } catch (error) {
        console.error(error)
      }
    },
    // Estilos para animaciones
    onEnter: function (el) {
      el.style.height = '0px'
      const height = el.scrollHeight
      requestAnimationFrame(() => { el.style.height = height + 'px' })
    },
    onAfterEnter: function (el) {
      el.style.height = ''
    },
    onLeave: function (el) {
      el.style.height = el.scrollHeight + 'px'
      requestAnimationFrame(() => { el.style.height = '0px' })
    },
  }
}
</script>

<style lang="scss" scoped>
.news-bar {
  @apply flex flex-wrap items-center gap-x-3 gap-y-2 w-full px-4 py-2 text-sm;
  @apply bg-primary-900 text-secondary-50;
  @apply dark:bg-darkblue-500;
}

.news-bar__content {
  @apply flex flex-wrap items-center gap-x-2 gap-y-1 flex-1 min-w-0 m-0 leading-normal font-medium;
}

.news-bar__badge {
  @apply inline-flex items-center flex-shrink-0 px-2 py-0.5 rounded-full uppercase tracking-wide text-xs font-bold;
  @apply bg-staticprimary-900 text-white;
}

.news-bar__text {
  @apply break-words;
}

.news-bar__link {
  @apply underline font-bold;

  &:hover {
    @apply no-underline;
  }
}

.news-bar__actions {
  @apply flex items-center flex-shrink-0 gap-x-1 ml-auto;
}

.news-bar__btn {
  @apply flex items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors duration-300;

  &:hover {
    @apply bg-primary-100 text-darkblue-800;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
}

.news-bar__link:focus-visible,
.news-bar__btn:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.news-bar-collapse-enter-active,
.news-bar-collapse-leave-active {
  @apply overflow-hidden;
  transition: height 300ms ease, opacity 300ms ease;
}

.news-bar-collapse-enter,
.news-bar-collapse-leave-to {
  @apply opacity-0;
}

@media (prefers-reduced-motion: reduce) {
  .news-bar-collapse-enter-active,
  .news-bar-collapse-leave-active,
  .news-bar__btn {
    transition: none;
  }
}
</style>
